import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FormField } from "@/pages/ContactPage";
import { SERVICE_OPTIONS } from "@/lib/content";

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export default function PortalNewWorkOrder() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    property_address: "",
    service_type: SERVICE_OPTIONS[0],
    priority: "normal",
    requested_date: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/work-orders", form);
      toast.success("Work order submitted.");
      navigate(`/portal/work-orders/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">New work order</div>
      <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">Submit a new work order</h1>
      <p className="mt-3 text-slate-600 text-sm max-w-xl">
        Provide the property details and service scope. Our operations team will
        acknowledge within one business day.
      </p>

      <form onSubmit={submit} data-testid="new-wo-form" className="mt-8 border border-slate-200 bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <FormField label="Property address" required id="prop" value={form.property_address} onChange={update("property_address")} testId="wo-address" />
        </div>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
            Service type <span className="text-[#1E40AF] ml-1">*</span>
          </span>
          <select
            data-testid="wo-service"
            required
            value={form.service_type}
            onChange={update("service_type")}
            className="w-full px-3 py-2 bg-white border border-slate-300 focus:border-[#1E40AF] outline-none text-sm rounded-sm"
          >
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Priority</span>
          <select
            data-testid="wo-priority"
            value={form.priority}
            onChange={update("priority")}
            className="w-full px-3 py-2 bg-white border border-slate-300 focus:border-[#1E40AF] outline-none text-sm rounded-sm"
          >
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>

        <FormField label="Requested date" type="date" id="rdate" value={form.requested_date} onChange={update("requested_date")} testId="wo-date" />

        <div className="md:col-span-2">
          <FormField label="Additional notes / scope" as="textarea" rows={5} id="notes" value={form.notes} onChange={update("notes")} testId="wo-notes" />
        </div>

        <div className="md:col-span-2 flex items-center justify-between">
          <div className="text-xs text-slate-500">You&apos;ll receive a portal notification when the order is assigned.</div>
          <button
            type="submit"
            data-testid="wo-submit-btn"
            disabled={loading}
            className="px-5 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60"
          >
            {loading ? "Submitting…" : "Submit work order"}
          </button>
        </div>
      </form>
    </div>
  );
}
